import { ImageMap } from '@shared/modules/images/model';
import { LocalDate } from '@shared/modules/dates';
import { Product as ProductModel } from '@modules/alert-reports/products/model';
import { z } from 'zod';
import { NonEmptyString } from '@shared/schemas';
import { Editorial } from '@shared/modules/editorial/model';
import { ReactComponent as IconTrendFalling } from '@assets/icons/alert-reports/stage-trend-falling.svg';
import { ReactComponent as IconTrendIncreasing } from '@assets/icons/alert-reports/stage-trend-increasing.svg';
import { ReactComponent as IconTrendSteady } from '@assets/icons/alert-reports/stage-trend-steady.svg';
import { FC } from 'react';
import * as React from 'react';
import { MantineColor } from '@mantine/core';
import { ordFromOrdering } from '@shared/utils/order';

export namespace Pest {
  export const CoachArticleId = z.number().brand('CoachArticleId');
  export type CoachArticleId = z.infer<typeof CoachArticleId>;

  export enum Name {
    ThaumetopoeaPityocampa = 'thaumetopoea-pityocampa',
    ThaumetopoeaProcessionea = 'thaumetopoea-processionea',
    RhynchophorusFerrugineus = 'rhynchophorus-ferrugineus',
    PaysandisiaArchon = 'paysandisia-archon',
    CydalimaPerspectalis = 'cydalima-perspectalis',
    MelolonthaMelolontha = 'melolontha-melolontha',
    CamerariaOhridella = 'cameraria-ohridella',
    CorythuchaCiliata = 'corythucha-ciliata',
  }

  export const Stage = z.string().brand('PestStage');
  export type Stage = z.infer<typeof Stage>;

  export namespace Risk {
    export enum Level {
      None = 'none',
      Low = 'low',
      Medium = 'medium',
      High = 'high',
    }

    export const levelOrdering: Record<Level, number> = {
      [Level.None]: 0,
      [Level.Low]: 1,
      [Level.Medium]: 2,
      [Level.High]: 3,
    };

    export const levelOrd = ordFromOrdering(levelOrdering);

    export const levelColor: Record<Level, MantineColor> = {
      [Level.None]: 'gray.3',
      [Level.Low]: 'primary.3',
      [Level.Medium]: 'primary.4',
      [Level.High]: 'primary.5',
    };
  }

  export namespace Description {
    export interface DateRange {
      start: LocalDate;
      end: LocalDate;
    }

    export interface Stage {
      name: Pest.Stage;
      level: Risk.Level;
    }
  }
  export interface Description {
    enum: Pest.Name;
    name: String;
    dateRange: Description.DateRange;
    image: ImageMap | null;
    advice: NonEmptyString | null;
    stages: Array<Description.Stage>;
    hidden: boolean;
    notifEnabled: boolean;
  }

  export namespace Detail {
    export enum Trend {
      Steady = 'steady',
      Falling = 'falling',
      Increasing = 'increasing',
    }

    export const trendIcon: Record<Trend, FC<React.ComponentProps<'svg'> & { title?: string }>> = {
      [Trend.Steady]: IconTrendSteady,
      [Trend.Falling]: IconTrendFalling,
      [Trend.Increasing]: IconTrendIncreasing,
    };

    export const trendLabel: Record<Trend, string> = {
      [Trend.Steady]: 'Stable',
      [Trend.Falling]: 'En baisse',
      [Trend.Increasing]: 'En augmentation',
    };

    export interface Stage {
      name: Pest.Stage;
      level: Risk.Level;
      trend: Trend;
    }

    export interface Coach {
      id: CoachArticleId;
      title: NonEmptyString;
      content: Editorial;
      order: number;
    }

    export interface Product {
      id: ProductModel.Id;
      label: NonEmptyString;
      image: ImageMap | null;
      brand: NonEmptyString;
    }
  }

  export interface Detail {
    enum: Pest.Name;
    name: string;
    dateRange: Pest.Description.DateRange;
    images: Array<ImageMap> | null;
    advice: NonEmptyString | null;
    stages: Array<Detail.Stage>;
    coachs: Array<Detail.Coach>;
    products: Array<Detail.Product>;
  }
}
